import { claimedDeskClientSchema } from "./desk-client-tools.js";
import { z } from "zod";
import { v2HybridChannelCardSchema } from "./v2-hybrid-capabilities.js";
import { runtimePluginRefSchema, runtimePluginSchema, } from "./runtime-plugins.js";
import { agentPromptSurfaceSchema } from "./agent-prompts.js";
import { executableTaskRunSummarySchema, taskRunSummarySchema } from "./task-runs.js";
import { taskSummarySchema } from "./tasks.js";
import { resolverEvalEnvelopeSchema } from "./resolver-eval.js";
import { activeSessionContractProjectionSchema } from "./session-contracts.js";
import { researchWarehouseProfileSchema } from "./research-warehouse.js";
import { DISABLED_MEMORY_ROLLOUT_MODE, INTERACTIVE_MEMORY_RUN_POLICY, memoryRolloutModeSchema, memoryRunPolicySchema, } from "./memory-policy.js";
import { intentionalMemoryDigestPacketSchema } from "./memory-packet.js";
import { accessExecutionGrantV1Schema, accessPolicyModeSchema, } from "./access-control.js";
export const taskOverviewSchema = z.object({
    task: taskSummarySchema,
    latestRun: taskRunSummarySchema.nullable(),
});
export const claimedTaskRunSchema = z.object({
    attemptNumber: z.number().int().positive(),
    run: executableTaskRunSummarySchema,
    task: taskSummarySchema,
    compiledPromptUrl: z.string().url().optional(),
    /** Earlier turns a rollback took out of this run's conversation. */
    excludedConversationRunIds: z.array(z.string().min(1)).max(500).default([]),
    electricDeadlineAt: z.number().int().positive().optional(),
    organisationId: z.string().trim().min(1).optional(),
    attachedViewId: z.string().trim().min(1).optional(),
    attachedChannelAdminAccess: z.boolean().default(false),
    allowedOrganisationIds: z.array(z.string()),
    warehouseProfile: researchWarehouseProfileSchema.optional(),
    agentPromptSurfaces: z.array(agentPromptSurfaceSchema).default([]),
    memoryDigest: z.string().default(""),
    memoryDigestPacket: intentionalMemoryDigestPacketSchema.optional(),
    memoryPolicy: memoryRunPolicySchema.default(INTERACTIVE_MEMORY_RUN_POLICY),
    memoryRolloutMode: memoryRolloutModeSchema.default(DISABLED_MEMORY_ROLLOUT_MODE),
    organizationStyle: z.string().max(500).optional(),
    runtimeChannelCards: z.array(v2HybridChannelCardSchema).max(500).optional(),
    runtimePlugins: z.array(runtimePluginSchema).default([]),
    availableRuntimePlugins: z.array(runtimePluginRefSchema).default([]),
    // Connections the user started but never completed (status='pending').
    // Excluded from routing; surfaced so the runtime can tell the model to ask
    // the user to finish connecting rather than inventing "checked and empty".
    pendingRuntimePlugins: z.array(runtimePluginRefSchema).default([]),
    // False only when nothing outside the workspace is connected for this
    // person; absent means unknown and the runtime says nothing about it.
    externalServicesConnected: z.boolean().optional(),
    // Whether channel widgets are on for the run's organisation: the executor offers the widget tools only then.
    channelWidgetsEnabled: z.boolean().default(false),
    // A run its session's bound Desk asked for, by the Desk's owner (docs/specs/twin-desk-client-tools.md §2.2): the
    // executor offers desk_call only with this, for the toolset named.
    deskClient: claimedDeskClientSchema.optional(),
    // A session Desk started (its client app is desk): the task agent's prompt gains the Desk surface block.
    deskSurface: z.boolean().default(false),
    // A session that has received a result from the person's Mac (spec §2.5): the executor refuses every side-effecting
    // tool in its turns, whoever drives them, until an owner-confirmation path exists.
    deskTainted: z.boolean().default(false),
    resolverEval: resolverEvalEnvelopeSchema.optional(),
    sessionContract: activeSessionContractProjectionSchema.optional(),
    accessPolicyMode: accessPolicyModeSchema.default("legacy"),
    executionGrants: z.array(accessExecutionGrantV1Schema).default([]),
}).superRefine((claim, refinement) => {
    if (claim.attachedChannelAdminAccess && !claim.task.channelId) {
        refinement.addIssue({
            code: "custom",
            path: ["attachedChannelAdminAccess"],
            message: "Attached-channel admin access requires a channel-bound task",
        });
    }
    if (claim.accessPolicyMode !== "legacy" && claim.executionGrants.length === 0) {
        refinement.addIssue({
            code: "custom",
            path: ["executionGrants"],
            message: "Non-legacy access policy claims require execution grants",
        });
    }
    if (claim.accessPolicyMode === "legacy" && claim.executionGrants.length > 0) {
        refinement.addIssue({
            code: "custom",
            path: ["executionGrants"],
            message: "Legacy access policy claims must not carry execution grants",
        });
    }
});
export const claimedTaskRunBatchSchema = z.object({
    claimed: z.array(claimedTaskRunSchema),
    nextShardCursor: z.number().int().min(0),
});
export const listTasksWithLatestRunsResponseSchema = z.array(taskOverviewSchema);
